import type { CountingSkusDto, CountListItemDto } from '@pl/domain';
import type { QueryClient, UseQueryResult } from '@tanstack/react-query';
import { type CountsQuery, qk } from '../../lib/queries';

export const countContext = (id: string) => `count:${id}`;

/**
 * How often a screen that shows an earlier result, or how an earlier count went, asks for the open
 * counts of that result's own site (I-1): the phone hears count signals only for the site it streams,
 * and only while its stream is alive.
 */
export const OPEN_POLL_MS = 10_000;

/*
 * While any count is open at a site, this phone shows no earlier count result at all — not a link,
 * not a chip that says how it went, not the result screen — for anyone. A pallet count and a SKU
 * count cover the same boxes from two sides, and a lead who needs a result mid-count reads it in the
 * office. And it fails closed: the phone only believes nothing is open here on a list of open counts
 * it asked for after it last went into a count, because a list from before says nothing about the
 * count now in the worker's hand, and offline it may never be asked for again.
 */

/** When this phone last went into a count, per site: a Start, a join, a resume. */
const enteredAt = new Map<string, number>();
/**
 * When this phone last heard a count start, finish or end at a site (`useLive`). Anything it holds
 * about that site's counts from before is out of date from that moment (N10).
 */
const signalAt = new Map<string, number>();
/**
 * When this phone's stream last stopped being live: dropped, or silent past its heartbeat (I-1). A
 * count may have started or finished anywhere while nothing could be heard, so from that moment
 * every site's lists are out of date, as after a count signal for each.
 */
let gapAt: number | undefined;
export function __resetCountStateForTests(): void {
  enteredAt.clear();
  signalAt.clear();
  gapAt = undefined;
}

/** A count signal for this site has just arrived. Never earlier than one before it. */
export function noteCountSignal(siteId: string): void {
  signalAt.set(siteId, Math.max(Date.now(), signalAt.get(siteId) ?? 0));
}
/** The stream has just stopped being live. Never earlier than a gap before it. */
export function noteStreamGap(): void {
  gapAt = Math.max(Date.now(), gapAt ?? 0);
}
/** The latest moment the site's counts may have moved unheard: its count signal, or a gap in the stream. */
export const countSignalAt = (siteId: string | undefined) => {
  if (!siteId) return undefined;
  const signal = signalAt.get(siteId);
  return signal === undefined ? gapAt : gapAt === undefined ? signal : Math.max(signal, gapAt);
};

const isOpenListOf = (siteId: string) => (q: { queryKey: readonly unknown[] }) => {
  const key = q.queryKey[1] as CountsQuery | undefined;
  return q.queryKey[0] === 'counts' && key?.status === 'open' && key.siteId === siteId;
};

/**
 * This phone is going into a count here. Every open-count list, and every answer to which SKUs are
 * being counted, asked for before this moment stops counting as an answer — one still in flight is
 * cancelled, so it cannot land afterwards looking fresh — and the count itself goes into the lists
 * already cached, so they say something is open.
 */
export function enterCount(
  qc: QueryClient,
  siteId: string,
  count?: Pick<CountListItemDto, 'id' | 'siteId' | 'scope' | 'skuId' | 'palletId' | 'palletCode' | 'status' | 'startedAt' | 'startedByName'>,
): void {
  const at = Math.max(Date.now(), (enteredAt.get(siteId) ?? 0) + 1);
  enteredAt.set(siteId, at);
  void qc.cancelQueries({ predicate: isOpenListOf(siteId) });
  void qc.cancelQueries({ queryKey: qk.countingSkus(siteId) });
  if (count?.status === 'open') {
    // Field by field: whatever the caller holds, only what an open list item carries goes in.
    const item: CountListItemDto = {
      id: count.id,
      siteId: count.siteId,
      scope: count.scope,
      skuId: count.skuId,
      palletId: count.palletId,
      palletCode: count.palletCode,
      status: 'open',
      startedAt: count.startedAt,
      startedByName: count.startedByName,
      finishedAt: null,
      result: null,
      cancelledBy: null,
      cancelledByName: null,
    };
    qc.setQueriesData<CountListItemDto[]>(
      { predicate: isOpenListOf(siteId) },
      (list) => (list && !list.some((c) => c.id === count.id) ? [item, ...list] : list),
      // Not an answer from the server: it must not read as a list asked for after this moment.
      { updatedAt: at },
    );
  }
}

/**
 * Whether a count is open at the site, as far as this phone may trust its list: `open`; `none`, only
 * on a list that arrived after this phone last went into a count here; otherwise `unknown` — still
 * on its way, paused offline, or never asked for — which shows no more than `open` does.
 *
 * A list the phone knows is out of date reads as counting: one older than the latest count signal
 * for the site or the latest gap in the stream (none at all since then included), or one whose last
 * reload failed. Where no signal can reach the phone, the screens ask for the list every
 * `OPEN_POLL_MS` (I-1).
 */
export function openCountState(
  opened: Pick<UseQueryResult<CountListItemDto[]>, 'data' | 'dataUpdatedAt' | 'errorUpdatedAt'>,
  siteId: string | undefined,
): 'open' | 'none' | 'unknown' {
  if (!siteId) return 'unknown';
  const signal = countSignalAt(siteId);
  if (signal !== undefined && (!opened.data || opened.dataUpdatedAt <= signal)) return 'open';
  if (opened.data && opened.errorUpdatedAt > opened.dataUpdatedAt) return 'open';
  if (!opened.data) return 'unknown';
  if (opened.data.some((c) => c.status === 'open')) return 'open';
  const entered = enteredAt.get(siteId);
  return entered !== undefined && opened.dataUpdatedAt <= entered ? 'unknown' : 'none';
}

/**
 * Which SKUs' box codes the Ship screen hides at the site: those under an open count there, because
 * a count expects a SKU's picked boxes too. `all` while the phone cannot tell — no answer yet, a
 * failed one, no site, or an answer asked for before this phone last went into a count here — as
 * `openCountState` fails closed.
 */
export function hiddenSkus(counting: Pick<UseQueryResult<CountingSkusDto>, 'data' | 'dataUpdatedAt' | 'isError'>, siteId: string | undefined): 'all' | ReadonlySet<string> {
  if (!siteId || !counting.data || counting.isError) return 'all';
  const entered = enteredAt.get(siteId);
  if (entered !== undefined && counting.dataUpdatedAt <= entered) return 'all';
  return new Set(counting.data.skuIds);
}
